import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const zeroStage3Component: ComponentDef = {
  id: 'zero-stage3',
  name: 'ZeRO Optimizer',
  description: 'ZeRO Stage 1/2/3: progressively shard optimizer states, gradients, and parameters across all GPUs to eliminate redundancy.',
  category: 'parallelism',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'n_gpus', label: 'GPUs', type: 'number', default: 8, min: 2, max: 1024, step: 1 },
    {
      name: 'stage',
      label: 'ZeRO Stage',
      type: 'select',
      default: 'stage3',
      options: [
        { label: 'Stage 1 — Optimizer state partition', value: 'stage1' },
        { label: 'Stage 2 — + Gradient partition', value: 'stage2' },
        { label: 'Stage 3 — + Parameter partition', value: 'stage3' }
      ]
    }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const n_gpus = Number(params.n_gpus);
    const stage = String(params.stage || 'stage3');

    const g = new GraphBuilder();
    g.input('x', [d_model]);

    if (stage === 'stage1') {
      // Stage 1: full params on each GPU, but optimizer state (m, v, fp32 copy) partitioned
      g.linear('fwd', 'x', [d_model, d_model]);
      g.activation('full_grad', 'fwd', 'Full gradients (replicated)');
      g.allreduce('grad_sync', 'full_grad', [d_model]);
      // Each GPU only updates its 1/N slice of optimizer state
      g.split('opt_shard', 'grad_sync', [d_model / n_gpus], 'opt-state');
      g.scale('adam_update', 'opt_shard', 'AdamW (m, v, fp32 — sharded 1/N)');
      g.allgather('param_sync', 'adam_update', [d_model]);
      g.output('out', 'param_sync');

    } else if (stage === 'stage2') {
      // Stage 2: full params, gradient AND optimizer state partitioned
      g.linear('fwd', 'x', [d_model, d_model]);
      // ReduceScatter: each GPU receives and owns 1/N slice of gradients
      g.reduceScatter('grad_shard', 'fwd', [d_model / n_gpus]);
      g.scale('adam_update', 'grad_shard', 'AdamW (grad + opt-state — sharded 1/N)');
      // AllGather params for next forward pass
      g.allgather('param_sync', 'adam_update', [d_model]);
      g.output('out', 'param_sync');

    } else {
      // Stage 3: params, gradients, AND optimizer state all partitioned
      // AllGather params just before forward pass, discard after
      g.split('param_shard', 'x', [d_model / n_gpus], 'params');
      g.allgather('params', 'param_shard', [d_model]);
      g.linear('fwd', 'params', [d_model, d_model]);
      // ReduceScatter gradients — each GPU keeps only its 1/N slice
      g.reduceScatter('grad_shard', 'fwd', [d_model / n_gpus]);
      g.scale('adam_update', 'grad_shard', 'AdamW (all states sharded 1/N)');
      g.output('out', 'adam_update');
    }

    return g.build();
  }
};
